import { type SsoProviderTypeEnum } from "@finway-group/shared/lib/models"
import localForage from "localforage"
import { queryClient } from "~Shared/config/reactQueryConfig"
import { ACCESS_TOKEN, RESET_REDUX_STORE_EVENT } from "~Shared/services/localstorage.consts"
import { SessionStorageService } from "~Shared/services/sessionstorage.service"

export { ACCESS_TOKEN } from "~Shared/services/localstorage.consts"
export const WEAVR_TOKEN = "weavrAccessToken"
export const SSO_PROVIDER_TYPES = "ssoProviderTypes"
export const HIDE_ABSENCE_SUBSTITUTES_INFO_BANNER = "hideAbsenceSubstitutesInfoBanner"
export const BANK_WEBFORM_LOCK_PREFIX = "bank-webform"

export const PURCHASE_ORDERS_TABLE_FILTERS = "purchaseOrdersTableFilters"

export const DELIVERY_NOTES_TABLE_FILTERS = "deliveryNotesTableFilters"

export const API_KEYS_TABLE_FILTERS = "apiKeysTableFilters"

export const PURCHASE_ORDERS_TABLE_LIMIT = "purchaseOrdersTableLimit"
export const PURCHASE_ORDERS_QUERY_PARAMS = "purchaseOrdersQueryParams"
export const DELIVERY_NOTES_TABLE_LIMIT = "deliveryNotesTableLimit"
export const DELIVERY_NOTES_QUERY_PARAMS = "deliveryNotesQueryParams"

const keepOnClear = [
    { key: SSO_PROVIDER_TYPES, startsWith: false },
    { key: HIDE_ABSENCE_SUBSTITUTES_INFO_BANNER, startsWith: true },
]

export const LocalStorageService = {
    isAuthorized: () => LocalStorageService.getAccessToken() !== null,

    getAuthenticationHeader: () => ({
        Authorization: `Bearer ${LocalStorageService.getAccessToken()}`,
        "x-weavr-token": LocalStorageService.get(WEAVR_TOKEN) ?? "",
    }),

    // weavr
    setWeavrToken: (value: string) => localStorage.setItem(WEAVR_TOKEN, value),
    unsetWeavrToken: () => localStorage.removeItem(WEAVR_TOKEN),

    // access token
    getAccessToken: () => {
        const accessToken = localStorage.getItem(ACCESS_TOKEN)
        return accessToken
    },
    setAccessToken: (value: string) => {
        localStorage.setItem(ACCESS_TOKEN, value)
        // We dispatch a custom event so every useLocalStorage hook is notified
        window.dispatchEvent(new Event("local-storage"))
    },
    /**
     * Deletes ALL local state
     * - local storage
     * - redux (back by local forage)
     * - react query data
     */
    unsetAccessToken: () => {
        if (!localStorage.getItem(ACCESS_TOKEN)) {
            return
        }

        localStorage.removeItem(ACCESS_TOKEN)
        // We dispatch a custom event so every useLocalStorage hook is notified
        window.dispatchEvent(new Event("local-storage"))
        window.dispatchEvent(new Event(RESET_REDUX_STORE_EVENT))

        LocalStorageService.clear()
        SessionStorageService.clearRedirectUrl()
        SessionStorageService.setIsLoggingOut()
        setTimeout(() => {
            SessionStorageService.clearIsLoggingOut()
        }, 1000)
        localForage.clear()

        queryClient.clear()
    },

    getSsoProviderTypes: () => (localStorage.getItem(SSO_PROVIDER_TYPES)?.split(",") as Array<SsoProviderTypeEnum>) ?? [],
    setSsoProviderTypes: ({ ssoProviderTypes }: { ssoProviderTypes: Array<SsoProviderTypeEnum> }) => {
        const existingSsoProviderTypes = localStorage.getItem(SSO_PROVIDER_TYPES)
        const ssoProviderTypesString = ssoProviderTypes.join(",")
        if (existingSsoProviderTypes !== ssoProviderTypesString) {
            localStorage.setItem(SSO_PROVIDER_TYPES, ssoProviderTypesString)
            // We dispatch a custom event so every useLocalStorage hook is notified
            window.dispatchEvent(new Event("local-storage"))
        }
    },

    get: (key: string) => localStorage.getItem(key),
    set: (key: string, value: string) => localStorage.setItem(key, value),
    remove: (key: string) => localStorage.removeItem(key),
    clear: () => {
        // prettier-ignore
        const keep: Array<{ key: string; value: string }> = Object.keys(localStorage)
            .filter((key) =>
                keepOnClear.some(({ key: keepKey, startsWith }) =>
                    (startsWith ? key.startsWith(keepKey) : key === keepKey)))
            .map((key) => {
                const existingValue = localStorage.getItem(key)
                if (existingValue !== undefined && existingValue !== null) {
                    return { key, value: existingValue }
                }
                return undefined
            })
            .filter((item) => !!item)

        localStorage.clear()

        keep.forEach(({ key, value }) => {
            localStorage.setItem(key, value)
        })
    },
}
